import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { catchAll } from '@sorare/routing';
import type { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  ANY_SPORT_PLAY_EVENTTYPE_ARENA,
  ANY_SPORT_PLAY_EVENTTYPE_ARENA_FIXTURE_LINEUPS,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS,
  ANY_SPORT_PLAY_QUICK_PLAY,
  ANY_SPORT_PLAY_SERIES,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';

type Props = {
  so5FixtureSlug?: string;
  eventType: So5FixtureEvent;
  children: ReactNode;
};

export const FixtureTabLayout = ({
  so5FixtureSlug,
  eventType,
  children,
}: Props): ReactNode => {
  const { currentUser } = useCurrentUserContext();
  const {
    flags: {
      useQuickPlay = {
        [Sport.BASEBALL]: false,
        [Sport.NBA]: false,
        [Sport.FOOTBALL]: false,
      },
      enableArenaV2 = false,
      enableSeriesOnboardingPacks = false,
    },
  } = useFeatureFlags();

  const { sport, generateSportContextPath } = useSportContext();

  if (!so5FixtureSlug) {
    return children;
  }

  const quickplayPath = generateSportContextPath(ANY_SPORT_PLAY_QUICK_PLAY);
  const arenaPath = generateSportContextPath(
    ANY_SPORT_PLAY_EVENTTYPE_ARENA_FIXTURE_LINEUPS,
    {
      params: {
        eventType: eventType.toLowerCase(),
        fixture: so5FixtureSlug,
      },
    }
  );

  return (
    <>
      <PageHeader text={navLabels.play} disableBackBrowser />
      <ContentWithStickyHeader
        header={
          <FullWidthContainer.NoPadding>
            <TabBar variant="flat" fullWidth>
              {!!useQuickPlay[sport] && (
                <Tab to={quickplayPath} matches={[catchAll(quickplayPath)]}>
                  <FormattedMessage {...glossary.quickPlay} />
                </Tab>
              )}
              <Tab
                to={generateSportContextPath(
                  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS,
                  {
                    params: {
                      fixture: so5FixtureSlug,
                      eventType: eventType.toLowerCase(),
                    },
                  }
                )}
              >
                <FormattedMessage {...navLabels.pro} />
              </Tab>
              {!!enableArenaV2 && (
                <Tab
                  matches={[catchAll(ANY_SPORT_PLAY_EVENTTYPE_ARENA)]}
                  to={arenaPath}
                >
                  <FormattedMessage {...navLabels.leaderboards} />
                </Tab>
              )}
              {!!currentUser?.anyUserSportProfile?.proUnlocked &&
                !!enableSeriesOnboardingPacks && (
                  <Tab to={generateSportContextPath(ANY_SPORT_PLAY_SERIES)}>
                    <FormattedMessage {...navLabels.set} />
                  </Tab>
                )}
            </TabBar>
          </FullWidthContainer.NoPadding>
        }
      >
        {children}
      </ContentWithStickyHeader>
    </>
  );
};
